import { useState } from 'react';
import {
    Loader2, Sparkles, Download, Printer, FileText,
    Lock, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import * as apiClient from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const FIELD_DEFS = [
    { key: 'candidateName',    label: 'Candidate Name',    placeholder: 'e.g. Riya Sharma',              required: true },
    { key: 'role',             label: 'Role / Position',   placeholder: 'e.g. Senior Frontend Engineer',  required: true },
    { key: 'department',       label: 'Department',        placeholder: 'e.g. Engineering' },
    { key: 'companyName',      label: 'Company Name',      placeholder: 'e.g. Acme Corp',                 required: true },
    { key: 'salary',           label: 'Salary / CTC',      placeholder: 'e.g. ₹18,00,000 per annum',     required: true },
    { key: 'joiningDate',      label: 'Date of Joining',   datePicker: true,                              required: true },
    { key: 'location',         label: 'Work Location',     placeholder: 'e.g. Bengaluru, India' },
    { key: 'workMode',         label: 'Work Mode',         placeholder: 'On-site / Hybrid / Remote' },
    { key: 'reportingManager', label: 'Reporting Manager', placeholder: 'e.g. Amit Verma' },
    { key: 'benefits',         label: 'Benefits',          placeholder: 'e.g. Health insurance, 24 PTO days…' },
    { key: 'extraNotes',       label: 'Additional Notes',  placeholder: 'Any special terms or conditions', textarea: true },
];

// Today in YYYY-MM-DD for the date input min attribute
const todayISO = new Date().toISOString().split('T')[0];

export default function OfferLetterModal({ open, onClose, processId, prefill = {}, onGenerated }) {
    const [details, setDetails] = useState(() => {
        const d = {};
        FIELD_DEFS.forEach(f => { d[f.key] = prefill[f.key] || ''; });
        return d;
    });
    const [generating, setGenerating] = useState(false);
    const [letter, setLetter] = useState(prefill.existingContent || null);
    const [showForm, setShowForm] = useState(!prefill.existingContent);

    const set = (key, val) => setDetails(prev => ({ ...prev, [key]: val }));

    const handleGenerate = async () => {
        const missing = FIELD_DEFS.filter(f => f.required && !details[f.key]?.trim()).map(f => f.label);
        if (missing.length) {
            toast.error(`Please fill in: ${missing.join(', ')}`);
            return;
        }
        setGenerating(true);
        try {
            const res = await apiClient.generateOfferLetter(processId, details);
            setLetter(res.data.content);
            setShowForm(false);
            toast.success('Offer letter generated!');
            onGenerated?.();
        } catch (err) {
            toast.error(err.message || 'Failed to generate offer letter.');
        } finally {
            setGenerating(false);
        }
    };

    const handlePrint = () => {
        const w = window.open('', '_blank');
        w.document.write(`
            <html><head>
            <title>Offer Letter — ${details.candidateName || 'Candidate'}</title>
            <style>
                body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 0 24px; color: #111; line-height: 1.7; }
                h1,h2,h3 { font-weight: bold; margin-top: 1.5em; }
                p { margin: 0.6em 0; }
                hr { border: none; border-top: 1px solid #ccc; margin: 1.5em 0; }
                .confidential { text-align:center; color:#999; font-size:0.75rem; text-transform:uppercase; letter-spacing:0.1em; margin-bottom:2em; }
            </style>
            </head><body>
            <div class="confidential">Confidential — Private &amp; Restricted</div>
            ${letterToHTML(letter)}
            </body></html>
        `);
        w.document.close();
        w.print();
    };

    const handleDownload = () => {
        const blob = new Blob([letter || ''], { type: 'text/plain' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `offer_letter_${(details.candidateName || 'candidate').replace(/\s+/g, '_')}.md`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-primary" />
                        Generate Offer Letter
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                        <Lock className="h-3 w-3" /> Confidential — visible to authorised HR staff only
                    </DialogDescription>
                </DialogHeader>

                {/* ── Details Form ─────────────────────────────────────── */}
                <div>
                    <button
                        type="button"
                        className="flex w-full items-center justify-between text-sm font-medium py-2"
                        onClick={() => setShowForm(v => !v)}
                    >
                        <span>{letter ? 'Edit Details' : 'Candidate & Offer Details'}</span>
                        {showForm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>

                    {showForm && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pb-4">
                            {FIELD_DEFS.map(f => (
                                <div key={f.key} className={cn('space-y-1.5', f.textarea && 'sm:col-span-2')}>
                                    <Label htmlFor={f.key} className="text-xs font-medium">
                                        {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                                    </Label>
                                    {f.datePicker ? (
                                        <input
                                            type="date"
                                            id={f.key}
                                            value={details[f.key]}
                                            min={todayISO}
                                            onChange={e => set(f.key, e.target.value)}
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        />
                                    ) : f.textarea ? (
                                        <Textarea
                                            id={f.key}
                                            value={details[f.key]}
                                            onChange={e => set(f.key, e.target.value)}
                                            placeholder={f.placeholder}
                                            rows={3}
                                            className="text-sm"
                                        />
                                    ) : (
                                        <Input
                                            id={f.key}
                                            value={details[f.key]}
                                            onChange={e => set(f.key, e.target.value)}
                                            placeholder={f.placeholder}
                                            className="text-sm"
                                        />
                                    )}
                                </div>
                            ))}
                            <div className="sm:col-span-2">
                                <Button
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    className="w-full sm:w-auto"
                                >
                                    {generating
                                        ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        : <Sparkles className="mr-2 h-4 w-4" />}
                                    {letter ? 'Regenerate Letter' : 'Generate Offer Letter'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Generated Letter ─────────────────────────────────── */}
                {letter && (
                    <>
                        <Separator />
                        <div className="flex items-center justify-between gap-2 pt-1">
                            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium">
                                <Lock className="h-3 w-3" /> Confidential
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-1.5 text-xs">
                                    <Printer className="h-3.5 w-3.5" /> Print
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5 text-xs">
                                    <Download className="h-3.5 w-3.5" /> Download
                                </Button>
                                <Button
                                    variant="ghost" size="sm"
                                    onClick={() => { setShowForm(true); }}
                                    disabled={generating}
                                    className="gap-1.5 text-xs text-muted-foreground"
                                >
                                    <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                                </Button>
                            </div>
                        </div>

                        <div className="rounded-lg border border-amber-200/60 dark:border-amber-800/40 bg-amber-50/30 dark:bg-amber-900/10 p-6 prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{letter}</ReactMarkdown>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Minimal markdown → HTML for print (no external dep needed at print time)
function letterToHTML(md) {
    if (!md) return '';
    return md
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/^---$/gm, '<hr>')
        .replace(/\n/g, '<br>');
}
